// FILE: src/services/sessionDraw.js
//
// prepareSessionQuestions — the async draw + hydration step of a learner
// session start (session-draw stage 3, PR 2). Framework-agnostic and
// dependency-injected (same style as sessionStartCoordinator) so the
// hydration rules are unit-testable without rendering <App/>.
//
// It runs between the coordinator's load and buildSession:
//
//   1. Route the loaded result into the correct store: full-content banks →
//      applyLoadedBank (questionStores); poolOnly metadata → applyLoadedPool
//      (metadataPools). A poolOnly result must NEVER reach questionStores.
//   2. Run the EXISTING selection engine (createSession, or
//      buildFullExamWithCases for case-section sims) against the effective
//      pool. The live questionStores slot still wins over the loaded result
//      (editor edits / bulk imports), exactly as before — and since the store
//      is always full-content, a populated store short-circuits hydration.
//   3. If the drawn questions are metadata-only (no content), fetch full rows
//      for EXACTLY the drawn ids (questions_fetch_session) and re-order them
//      to the drawn sequence. Content is therefore complete before the
//      session ever renders (grading reads correctAnswers mid-session).
//   4. If any drawn id is missing from the fetch (stale cached pool — e.g. a
//      row hard-deleted after the pool was cached), invalidate the cert's
//      pool cache and throw: the coordinator surfaces its normal retryable
//      error, and the retry re-draws from a FRESH pool. We never silently
//      drop questions — that would shift case-block indexes and break
//      placements.
//
// No Redux. Pure async helpers.

import { createSession } from './questionService';
import { buildFullExamWithCases } from './examCaseBlocks';
import { getExamConfig } from '../config/exams';

/**
 * @typedef {Object} DrawDeps
 * @property {Set<string>} quarantinedIds     Open-report question ids.
 * @property {any[]|null}  store              questionStores[certId] (full content or null).
 * @property {any[]|null}  caseBlocks         Prefetched exam case blocks (or null).
 * @property {boolean}     caseExamsEnabled   isFeatureEnabled('caseStudiesInExamsEnabled').
 * @property {(certId:string, questions:any[])=>void} applyLoadedBank  questionStores writer (non-clobber).
 * @property {(certId:string, questions:any[])=>void} applyLoadedPool  metadataPools writer (non-clobber).
 * @property {(examId:string, ids:string[], opts?:object)=>Promise<any[]|null>} loadQuestionsByIds
 * @property {(certId:string)=>void} invalidateCertPool
 * @property {boolean} [deferExplanations]  Stage 3 PR 3: exclude the
 *           explanation column from hydration; rows arrive marked
 *           explanationDeferred and load on demand at submit / session end.
 */

/** True when a drawn question is metadata-only (came from the pool). */
const isContentFree = (q) => q != null && q.question === undefined;

/**
 * @param {import('./sessionStartCoordinator').StartDescriptor} descriptor
 * @param {any} result   CertBankResult from ensureCertLoaded/ensureCertPool.
 * @param {DrawDeps} deps
 * @returns {Promise<{questions:any[], caseAssembly:(object|null)}>}
 */
export async function prepareSessionQuestions(descriptor, result, deps) {
  const { certId, kind, mode } = descriptor;
  const {
    quarantinedIds, store, caseBlocks, caseExamsEnabled,
    applyLoadedBank, applyLoadedPool, loadQuestionsByIds, invalidateCertPool,
  } = deps;
  const config = getExamConfig(certId);

  // 1. Owned store routing — poolOnly results must never touch questionStores.
  if (result.poolOnly === true) applyLoadedPool(certId, result.questions);
  else applyLoadedBank(certId, result.questions);

  // 2. Draw with the EXISTING engine. Store preference is unchanged: a
  // populated (always full-content) store wins over the loaded result.
  const effectivePool = store != null ? store : result.questions;

  const options = { excludeIds: quarantinedIds };
  if (kind === 'targetedTest') { options.filters = descriptor.filters; options.count = descriptor.count; }
  if (kind === 'targeted')     { options.phases = descriptor.phases; }

  let caseAssembly = null;
  if (mode === 'full'
      && caseExamsEnabled
      && config.fullExam?.caseSection?.count > 0
      && Array.isArray(caseBlocks)
      && caseBlocks.length > 0) {
    caseAssembly = buildFullExamWithCases(effectivePool, config, caseBlocks,
      { excludeIds: quarantinedIds });
  }
  const drawn = caseAssembly
    ? caseAssembly.questions
    : createSession(certId, mode, effectivePool, options);

  // 3. Hydrate metadata-only rows (case-block questions arrive full from the
  // get_exam_case_blocks RPC and are skipped).
  const needsContent = drawn.filter(isContentFree);
  if (needsContent.length === 0) {
    return { questions: drawn, caseAssembly };
  }

  const rows = await loadQuestionsByIds(certId, needsContent.map((q) => String(q.id)),
    { excludeExplanation: deps.deferExplanations === true });
  const byId = rows === null
    ? null
    : new Map(rows.map((q) => [String(q.id), q]));

  if (byId === null || needsContent.some((q) => !byId.has(String(q.id)))) {
    // 4. Fetch failed or the cached pool is stale — refresh the pool so the
    // learner's Retry re-draws against reality, then fail retryably.
    invalidateCertPool(certId);
    throw new Error('Could not load the session questions. Please try again.');
  }

  const questions = drawn.map((q) => (isContentFree(q) ? byId.get(String(q.id)) : q));
  return {
    questions,
    caseAssembly: caseAssembly ? { ...caseAssembly, questions } : null,
  };
}

export default prepareSessionQuestions;
